import { z } from "zod";
import { BOOK_IDS, FORMAT_IDS, GENDERS } from "@shared/schemas";

/**
 * Shape of a personalize-wizard draft as it appears in a shareable URL fragment.
 * Every field is optional — a freshly-opened wizard encodes to {step:0} with
 * empty fields, and we hydrate whichever fields the incoming draft supplies.
 */
const draftSchema = z.object({
  step: z.number().int().min(0).max(5).optional(),
  name: z.string().max(12).optional(),
  gender: z.enum(GENDERS).nullable().optional(),
  modelId: z.string().max(8).nullable().optional(),
  dedication: z.string().max(280).optional(),
  signature: z.string().max(60).optional(),
  format: z.enum(FORMAT_IDS).nullable().optional(),
  bookId: z.enum(BOOK_IDS).optional(),
});

export type PersonalizeDraft = z.infer<typeof draftSchema>;

const FRAGMENT_PREFIX = "s=";

function base64urlEncode(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function base64urlDecode(s: string): Uint8Array {
  const padLen = (4 - (s.length % 4)) % 4;
  const std = s.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat(padLen);
  const bin = atob(std);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

const hasCompressionStream = typeof CompressionStream !== "undefined";

async function gzip(bytes: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(new CompressionStream("gzip"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

async function gunzip(bytes: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(new DecompressionStream("gzip"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/**
 * Encode a personalize draft into a URL-safe string. Uses gzip + base64url
 * when CompressionStream is available (typical browsers from 2023+); falls
 * back to plain base64url JSON when it isn't.
 *
 * The leading byte of the encoded payload signals which path was taken:
 *   "g" — gzip(JSON), base64url
 *   "p" — JSON, base64url (plain)
 */
export async function encodeDraft(draft: PersonalizeDraft): Promise<string> {
  const json = JSON.stringify(draft);
  const jsonBytes = new TextEncoder().encode(json);
  if (hasCompressionStream) {
    const compressed = await gzip(jsonBytes);
    return "g" + base64urlEncode(compressed);
  }
  return "p" + base64urlEncode(jsonBytes);
}

/**
 * Decode a URL-safe string produced by `encodeDraft`. Returns null when the
 * payload is malformed, the embedded JSON doesn't validate, or any step in
 * the pipeline throws. Never throws.
 */
export async function decodeDraft(s: string): Promise<PersonalizeDraft | null> {
  try {
    if (s.length < 2) return null;
    const tag = s[0]!;
    const body = s.slice(1);
    const bytes = base64urlDecode(body);
    let jsonText: string;
    if (tag === "g") {
      if (!hasCompressionStream) return null;
      jsonText = new TextDecoder().decode(await gunzip(bytes));
    } else if (tag === "p") {
      jsonText = new TextDecoder().decode(bytes);
    } else {
      return null;
    }
    const parsed = draftSchema.safeParse(JSON.parse(jsonText));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/**
 * Read `#s=...` from window.location.hash and return the encoded blob, or null
 * if absent. Pure window read — safe to call on every render.
 */
export function readDraftFromLocation(): string | null {
  if (typeof window === "undefined") return null;
  const hash = window.location.hash.replace(/^#/, "");
  if (!hash.startsWith(FRAGMENT_PREFIX)) return null;
  return hash.slice(FRAGMENT_PREFIX.length) || null;
}

/**
 * Write `#s=<blob>` into the URL fragment without growing the history stack.
 * No-op when running outside a browser.
 */
export function writeDraftFragment(encoded: string): void {
  if (typeof window === "undefined") return;
  const url = window.location.pathname + window.location.search + "#" + FRAGMENT_PREFIX + encoded;
  window.history.replaceState(null, "", url);
}

/**
 * Strip the `#s=...` fragment from the current URL (used when leaving the
 * wizard so the cart/checkout pages aren't carrying a stale draft).
 */
export function clearDraftFragment(): void {
  if (typeof window === "undefined") return;
  if (!window.location.hash) return;
  window.history.replaceState(null, "", window.location.pathname + window.location.search);
}
